import React, { useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Divider,
  Alert,
  Typography,
  Space,
  Tag,
  Tooltip,
  message,
  Steps
} from 'antd';
import {
  ArrowLeftOutlined,
  ShopOutlined,
  UserOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  GlobalOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ApiOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { routes } from '../../utils/routes.config';
import { successMsg, errorMsg } from '../../utils/ShowMessage';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  primary: '#4f46e5',
  primaryHover: '#4338ca',
  primaryLight: '#eef2ff',
  primaryBorder: '#c7d2fe',
  success: '#16a34a',
  successLight: '#f0fdf4',
  successBorder: '#bbf7d0',
  danger: '#dc2626',
  dangerLight: '#fef2f2',
  dangerBorder: '#fecaca',
  warning: '#d97706',
  warningLight: '#fffbeb',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  text: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  radius: 12,
  radiusSm: 8,
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 24px rgba(0,0,0,0.08)',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, subtitle, step }: { icon: React.ReactNode; title: string; subtitle: string; step: number }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 28,
    padding: '20px 24px', background: C.primaryLight,
    borderRadius: C.radius, border: `1px solid ${C.primaryBorder}`
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: '50%', background: C.primary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: 18, flexShrink: 0
    }}>
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Title level={4} style={{ margin: 0, color: C.text, fontWeight: 700 }}>{title}</Title>
        <Tag style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 20, fontSize: 11, padding: '0 10px' }}>
          Step {step}
        </Tag>
      </div>
      <Text style={{ color: C.textSecondary, fontSize: 13, marginTop: 2, display: 'block' }}>{subtitle}</Text>
    </div>
  </div>
);

const FieldGroup = ({ children, label, icon }: { children: React.ReactNode; label: string; icon?: React.ReactNode }) => (
  <div style={{ marginBottom: 4 }}>
    {icon && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ color: C.primary, fontSize: 13 }}>{icon}</span>
        <Text style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
      </div>
    )}
    {children}
  </div>
);

const KeyRow = ({ label, value, danger, onCopy }: { label: string; value: string; danger?: boolean; onCopy: () => void }) => (
  <div style={{
    padding: '14px 18px',
    background: danger ? C.dangerLight : C.surfaceAlt,
    borderRadius: C.radiusSm,
    border: `1px solid ${danger ? C.dangerBorder : C.border}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12
  }}>
    <div style={{ minWidth: 0, flex: 1 }}>
      <Text style={{ fontSize: 11, fontWeight: 600, color: danger ? C.danger : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>
        {label}
      </Text>
      <Text code style={{ fontSize: 13, fontWeight: 600, color: danger ? C.danger : C.text, wordBreak: 'break-all' }}>
        {value}
      </Text>
    </div>
    <Tooltip title={`Copy ${label}`}>
      <Button
        size="small"
        icon={<CopyOutlined />}
        danger={danger}
        onClick={onCopy}
        style={{ flexShrink: 0, borderRadius: 6 }}
      />
    </Tooltip>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────

const CreateStore = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [createdData, setCreatedData] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied!`);
  };

  const checkEmailAvailability = async (email: string) => {
    try {
      const res = await ApiServices('get', `${ApiConfig.CHECK_ADMIN_EMAIL}?email=${encodeURIComponent(email.trim())}`);
      if (res && res.exists) {
        return { exists: true, message: res.message || 'A user with this email already exists' };
      }
      return { exists: false };
    } catch (err: any) {
      console.error('Email verification error:', err);
      return { exists: false, error: err.message };
    }
  };

  const validateStep = async (step: number) => {
    const stepFields: Record<number, string[]> = {
      0: ['storeName', 'shopUrl', 'platformType', 'accessScope', 'accessToken', 'details'],
      1: ['adminFirstName', 'adminLastName', 'adminEmail', 'adminPassword'],
    };
    try {
      await form.validateFields(stepFields[step]);
      return true;
    } catch {
      return false;
    }
  };

  const goNext = async () => {
    const valid = await validateStep(currentStep);
    if (!valid) return;

    if (currentStep === 1) {
      const email = form.getFieldValue('adminEmail')?.trim();
      if (email) {
        setCheckingEmail(true);
        try {
          const check = await checkEmailAvailability(email);
          if (check.exists) {
            const errorText = check.message || 'A user with this email already exists';
            form.setFields([
              {
                name: 'adminEmail',
                errors: [errorText],
              },
            ]);
            errorMsg(errorText);
            setCheckingEmail(false);
            return;
          }
        } catch {
          // allow catch error handling
        } finally {
          setCheckingEmail(false);
        }
      }
    }

    setCurrentStep(s => s + 1);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const payload = {
        name: values.storeName,
        shopUrl: values.shopUrl,
        accessScope: values.accessScope,
        accessToken: values.accessToken,
        details: values.details,
        platformType: values.platformType || 'shopify',
        image: values.image || null,
        adminEmail: values.adminEmail,
        adminPassword: values.adminPassword,
        adminFirstName: values.adminFirstName,
        adminLastName: values.adminLastName,
        adminPhoneNumber: values.adminPhoneNumber,
        adminGender: values.adminGender,
        adminCountry: values.adminCountry,
        adminState: values.adminState,
        adminCity: values.adminCity
      };
      const res = await ApiServices('post', ApiConfig.STORE_ONBOARDING, payload);
      if (res.success) {
        setCreatedData(res.data);
        form.resetFields();
        setCurrentStep(0);
        successMsg('Store onboarded successfully!');
      } else {
        errorMsg(res.message || 'Failed to onboard store');
      }
    } catch (error: any) {
      errorMsg(error?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success View ─────────────────────────────────────────────────────────────
  if (createdData) {
    return (
      <MainLayout>
        <div style={{ maxWidth: 900, margin: '0 auto', paddingBottom: 48 }}>
          {/* Success Banner */}
          <div style={{
            background: `linear-gradient(135deg, ${C.success} 0%, #15803d 100%)`,
            borderRadius: C.radius, padding: '32px 36px', marginBottom: 28,
            display: 'flex', alignItems: 'center', gap: 20, color: '#fff'
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0
            }}>
              <CheckCircleOutlined />
            </div>
            <div>
              <Title level={3} style={{ color: '#fff', margin: 0, fontWeight: 700 }}>Store Onboarded Successfully!</Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, marginTop: 4, display: 'block' }}>
                The store and admin account have been created. Save the API keys below — the secret key is shown only once.
              </Text>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexShrink: 0 }}>
              <Button size="large" style={{ borderRadius: C.radiusSm, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff' }}
                icon={<PlusOutlined />}
                onClick={() => { setCreatedData(null); }}
              >
                Add Another
              </Button>
              <Button size="large" type="primary" style={{ borderRadius: C.radiusSm, background: '#fff', color: C.success, border: 'none', fontWeight: 600 }}
                onClick={() => navigate(routes.SUPER_DASHBOARD)}
              >
                View All Stores
              </Button>
            </div>
          </div>

          <Row gutter={[20, 20]}>
            {/* Store Info */}
            <Col xs={24} md={12}>
              <Card bordered={false} style={{ borderRadius: C.radius, border: `1px solid ${C.border}`, boxShadow: C.shadow, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ width: 36, height: 36, borderRadius: C.radiusSm, background: C.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary }}>
                    <ShopOutlined style={{ fontSize: 16 }} />
                  </div>
                  <Text style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Store Details</Text>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { label: 'Store Name', value: createdData.store?.name },
                    { label: 'Shop URL', value: createdData.store?.shopUrl, isLink: true },
                    { label: 'Platform', value: createdData.store?.platformType || 'Shopify', isTag: true },
                  ].map(item => (
                    <div key={item.label} style={{ padding: '10px 14px', background: C.surfaceAlt, borderRadius: C.radiusSm, border: `1px solid ${C.borderLight}` }}>
                      <Text style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>{item.label}</Text>
                      {item.isLink
                        ? <a href={item.value} target="_blank" rel="noopener noreferrer" style={{ color: C.primary, fontWeight: 600, fontSize: 14 }}>{item.value}</a>
                        : item.isTag
                          ? <Tag color="blue" style={{ borderRadius: 20, fontWeight: 600 }}>{item.value}</Tag>
                          : <Text style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{item.value}</Text>
                      }
                    </div>
                  ))}
                </div>
              </Card>
            </Col>

            {/* Admin Info */}
            <Col xs={24} md={12}>
              <Card bordered={false} style={{ borderRadius: C.radius, border: `1px solid ${C.border}`, boxShadow: C.shadow, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ width: 36, height: 36, borderRadius: C.radiusSm, background: '#fdf4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9333ea' }}>
                    <UserOutlined style={{ fontSize: 16 }} />
                  </div>
                  <Text style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Admin Account</Text>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { label: 'Email', value: createdData.adminUser?.email },
                    { label: 'Role', value: 'Store Admin', isTag: true, tagColor: 'purple' },
                  ].map(item => (
                    <div key={item.label} style={{ padding: '10px 14px', background: C.surfaceAlt, borderRadius: C.radiusSm, border: `1px solid ${C.borderLight}` }}>
                      <Text style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>{item.label}</Text>
                      {item.isTag
                        ? <Tag color={item.tagColor || 'blue'} style={{ borderRadius: 20, fontWeight: 600 }}>{item.value}</Tag>
                        : <Text style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{item.value}</Text>
                      }
                    </div>
                  ))}
                </div>
              </Card>
            </Col>

            {/* API Keys */}
            <Col xs={24}>
              <Card bordered={false} style={{ borderRadius: C.radius, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ width: 36, height: 36, borderRadius: C.radiusSm, background: C.warningLight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.warning }}>
                    <ApiOutlined style={{ fontSize: 16 }} />
                  </div>
                  <div>
                    <Text style={{ fontWeight: 700, fontSize: 15, color: C.text }}>API Access Keys</Text>
                  </div>
                </div>
                <Alert
                  message="Save the Secret Key now — it will not be shown again"
                  description={createdData.apiKeys?.warning || 'Store this in a password manager or secure vault immediately.'}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16, borderRadius: C.radiusSm }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <KeyRow label="API Key" value={createdData.apiKeys?.apiKey} onCopy={() => handleCopy(createdData.apiKeys?.apiKey, 'API Key')} />
                  <KeyRow label="Secret Key" value={createdData.apiKeys?.secretKey} danger onCopy={() => handleCopy(createdData.apiKeys?.secretKey, 'Secret Key')} />
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </MainLayout>
    );
  }

  // ── Form View ────────────────────────────────────────────────────────────────
  const steps = [
    { title: 'Store Info', icon: <ShopOutlined /> },
    { title: 'Admin Account', icon: <UserOutlined /> },
    { title: 'Location', icon: <EnvironmentOutlined /> },
  ];

  return (
    <MainLayout>
      <div style={{ maxWidth: 860, margin: '0 auto', paddingBottom: 48 }}>

        {/* Page Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(routes.SUPER_DASHBOARD)}
              style={{ borderRadius: C.radiusSm, border: `1px solid ${C.border}`, color: C.textSecondary, background: C.surface }}
            >
              Back
            </Button>
            <div>
              <Title level={3} style={{ margin: 0, fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>
                Add New Store
              </Title>
              <Text style={{ color: C.textSecondary, fontSize: 13 }}>
                Onboard a merchant store and create its admin account
              </Text>
            </div>
          </div>
        </div>

        {/* Steps Progress */}
        <Card bordered={false} style={{ borderRadius: C.radius, border: `1px solid ${C.border}`, boxShadow: C.shadow, marginBottom: 20, padding: '4px 8px' }}>
          <Steps
            current={currentStep}
            size="small"
            style={{ padding: '4px 8px' }}
            items={steps.map((s, i) => ({
              title: <span style={{ fontSize: 13, fontWeight: currentStep === i ? 700 : 500, color: currentStep === i ? C.primary : currentStep > i ? C.success : C.textMuted }}>{s.title}</span>,
              icon: (
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: currentStep > i ? C.success : currentStep === i ? C.primary : C.borderLight,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14
                }}>
                  {currentStep > i ? <CheckCircleOutlined /> : s.icon}
                </div>
              )
            }))}
          />
        </Card>

        {/* Form Card */}
        <Card
          bordered={false}
          style={{ borderRadius: C.radius, border: `1px solid ${C.border}`, boxShadow: C.shadowMd }}
          styles={{ body: { padding: '32px 36px' } }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            initialValues={{ platformType: 'shopify' }}
            requiredMark={false}
          >
            {/* ── Step 0: Store Information ───────────────────────────────────── */}
            <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
              <SectionHeader
                icon={<ShopOutlined />}
                title="Store Information"
                subtitle="Enter the store identity, platform credentials, and API access details"
                step={1}
              />

              <FieldGroup label="Basic Details" icon={<InfoCircleOutlined />}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Store Name</span>}
                      name="storeName"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input prefix={<ShopOutlined style={{ color: C.textMuted }} />} placeholder="Acme Fashion Store" size="large" style={{ borderRadius: C.radiusSm }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Platform Type</span>}
                      name="platformType"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select placeholder="Select platform" size="large" style={{ borderRadius: C.radiusSm }}>
                        <Option value="shopify">Shopify</Option>
                        <Option value="woocommerce">WooCommerce</Option>
                        <Option value="magento">Magento</Option>
                        <Option value="custom">Custom</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Shop URL</span>}
                      name="shopUrl"
                      rules={[
                        { required: true, message: 'Required' },
                        { type: 'url', message: 'Enter a valid URL (https://...)' }
                      ]}
                    >
                      <Input prefix={<GlobalOutlined style={{ color: C.textMuted }} />} placeholder="https://your-store.myshopify.com" size="large" style={{ borderRadius: C.radiusSm }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Logo URL <Text style={{ color: C.textMuted, fontSize: 12 }}>(optional)</Text></span>}
                      name="image"
                    >
                      <Input placeholder="https://..." size="large" style={{ borderRadius: C.radiusSm }} />
                    </Form.Item>
                  </Col>
                </Row>
              </FieldGroup>

              <Divider style={{ margin: '8px 0 20px', borderColor: C.borderLight }} />

              <FieldGroup label="API Credentials" icon={<KeyOutlined />}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Access Scope</span>}
                      name="accessScope"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input
                        prefix={<SafetyCertificateOutlined style={{ color: C.textMuted }} />}
                        placeholder="read_orders, write_orders"
                        size="large"
                        style={{ borderRadius: C.radiusSm }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Access Token</span>}
                      name="accessToken"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input.Password
                        prefix={<KeyOutlined style={{ color: C.textMuted }} />}
                        placeholder="Merchant access token"
                        size="large"
                        style={{ borderRadius: C.radiusSm }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Store Notes</span>}
                      name="details"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <TextArea rows={3} placeholder="Describe the store, special shipping preferences, or any important notes..." style={{ borderRadius: C.radiusSm }} />
                    </Form.Item>
                  </Col>
                </Row>
              </FieldGroup>
            </div>

            {/* ── Step 1: Admin Credentials ──────────────────────────────────── */}
            <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
              <SectionHeader
                icon={<UserOutlined />}
                title="Admin Account"
                subtitle="Create the store administrator login credentials"
                step={2}
              />

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>First Name</span>}
                    name="adminFirstName"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input prefix={<UserOutlined style={{ color: C.textMuted }} />} placeholder="First name" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Last Name</span>}
                    name="adminLastName"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input prefix={<UserOutlined style={{ color: C.textMuted }} />} placeholder="Last name" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Email Address</span>}
                    name="adminEmail"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'email', message: 'Enter a valid email' },
                      {
                        validator: async (_, value) => {
                          if (!value) return Promise.resolve();
                          const trimmed = String(value).trim();
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(trimmed)) return Promise.resolve();

                          const check = await checkEmailAvailability(trimmed);
                          if (check.exists) {
                            return Promise.reject(new Error(check.message || 'A user with this email already exists'));
                          }
                          return Promise.resolve();
                        },
                        validateTrigger: 'onBlur',
                      }
                    ]}
                  >
                    <Input prefix={<MailOutlined style={{ color: C.textMuted }} />} placeholder="admin@store.com" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Password</span>}
                    name="adminPassword"
                    rules={[
                      { required: true, message: 'Required' },
                      { min: 6, message: 'Minimum 6 characters' }
                    ]}
                  >
                    <Input.Password prefix={<LockOutlined style={{ color: C.textMuted }} />} placeholder="Min. 6 characters" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* ── Step 2: Location (optional) ────────────────────────────────── */}
            <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
              <SectionHeader
                icon={<EnvironmentOutlined />}
                title="Location & Profile"
                subtitle="Optional details for the admin's profile — skip any fields that don't apply"
                step={3}
              />

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Phone <Text style={{ color: C.textMuted, fontSize: 12 }}>(optional)</Text></span>}
                    name="adminPhoneNumber"
                  >
                    <Input prefix={<PhoneOutlined style={{ color: C.textMuted }} />} placeholder="+1 234 567 8901" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Gender <Text style={{ color: C.textMuted, fontSize: 12 }}>(optional)</Text></span>}
                    name="adminGender"
                  >
                    <Select placeholder="Select gender" size="large" style={{ borderRadius: C.radiusSm }}>
                      <Option value="male">Male</Option>
                      <Option value="female">Female</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Country <Text style={{ color: C.textMuted, fontSize: 12 }}>(optional)</Text></span>}
                    name="adminCountry"
                  >
                    <Input placeholder="India / USA" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>State <Text style={{ color: C.textMuted, fontSize: 12 }}>(optional)</Text></span>}
                    name="adminState"
                  >
                    <Input placeholder="Gujarat / California" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>City <Text style={{ color: C.textMuted, fontSize: 12 }}>(optional)</Text></span>}
                    name="adminCity"
                  >
                    <Input placeholder="Ahmedabad / Los Angeles" size="large" style={{ borderRadius: C.radiusSm }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Summary preview before submit */}
              <div style={{
                marginTop: 8, padding: '16px 20px', background: C.primaryLight,
                borderRadius: C.radiusSm, border: `1px solid ${C.primaryBorder}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <CheckCircleOutlined style={{ color: C.primary }} />
                  <Text style={{ fontWeight: 700, fontSize: 13, color: C.primary }}>Ready to submit</Text>
                </div>
                <Text style={{ fontSize: 12, color: C.textSecondary }}>
                  Clicking <strong>Create Store</strong> will onboard the store, generate API keys, and create the admin account. This action cannot be undone.
                </Text>
              </div>
            </div>

            {/* ── Footer Navigation ──────────────────────────────────────────── */}
            <div style={{
              marginTop: 32, paddingTop: 24,
              borderTop: `1px solid ${C.borderLight}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <Button
                size="large"
                style={{ borderRadius: C.radiusSm, border: `1px solid ${C.border}`, color: C.textSecondary }}
                onClick={() => currentStep === 0 ? navigate(routes.SUPER_DASHBOARD) : setCurrentStep(s => s - 1)}
              >
                {currentStep === 0 ? 'Cancel' : <><ArrowLeftOutlined style={{ marginRight: 6 }} />Back</>}
              </Button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Step dots */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {steps.map((_, i) => (
                    <div key={i} style={{
                      width: i === currentStep ? 24 : 8, height: 8, borderRadius: 4,
                      background: i === currentStep ? C.primary : i < currentStep ? C.success : C.borderLight,
                      transition: 'all 0.2s ease'
                    }} />
                  ))}
                </div>

                {currentStep < 2 ? (
                  <Button
                    type="primary"
                    size="large"
                    loading={checkingEmail}
                    onClick={goNext}
                    style={{
                      borderRadius: C.radiusSm, background: C.primary,
                      border: 'none', fontWeight: 600, paddingLeft: 24, paddingRight: 24
                    }}
                  >
                    Continue <ArrowRightOutlined />
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    icon={<CheckCircleOutlined />}
                    style={{
                      borderRadius: C.radiusSm,
                      background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryHover} 100%)`,
                      border: 'none', fontWeight: 700, paddingLeft: 28, paddingRight: 28
                    }}
                  >
                    {loading ? 'Creating Store...' : 'Create Store'}
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CreateStore;
